import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, FileText, RefreshCw, Eye } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const SECTION_IMAGES = {
  scope: "https://customer-assets.emergentagent.com/job_athletics-platform/artifacts/gydtlpl2_AU20AC-OpenC-Day01-Pm-119-1-768x548-1.jpg",
  employment: "https://customer-assets.emergentagent.com/job_athletics-platform/artifacts/1my1fqv4_269154ac-7946-4df6-952f-607b3c1e9e0e.jpeg",
  training: "https://customer-assets.emergentagent.com/job_athletics-platform/artifacts/vcbzeci2_20250526_Technical-Meeting-Press-Conference-Practice_EK1_4318.jpg",
};

const bottomIcons = {
  disciplinary: FileText,
  review: RefreshCw,
  monitoring: Eye,
};
const AdditionalSections = () => {
  const { t, isRTL } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setIsVisible(true),
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const sections = t("additional.sections") || [];
  const bottom = t("additional.bottom") || [];
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  return (
    <section id="training" ref={sectionRef}>
      {Array.isArray(sections) &&
        sections.map((section, index) => (
          <div
            key={section.id || index}
            className={`grid lg:grid-cols-2 min-h-[400px] ${
              isVisible ? "opacity-100" : "opacity-0"
            } transition-opacity duration-700`}
            style={{ transitionDelay: `${index * 200}ms` }}
          >
            <div
              className={`p-12 lg:p-16 flex flex-col justify-center ${
                index % 2 === 0
                  ? "bg-[#E8792B] text-white order-1 lg:order-1"
                  : "bg-[#1a1a2e] text-white order-1 lg:order-2"
              }`}
            >
              <h3 className="text-2xl md:text-3xl font-bold mb-2">{section.title}</h3>
              <div className="w-16 h-1 bg-white/40 rounded-full mb-6" />
              <p className="text-white/90 text-base leading-relaxed mb-6">{section.text}</p>
              <button className="flex items-center gap-2 text-white font-medium text-sm hover:gap-3 transition-all group">
                <span>{t("additional.readMore")}</span>
                <Arrow className="w-4 h-4 transition-transform" />
              </button>
            </div>

            <div
              className={`relative overflow-hidden ${
                index % 2 === 0 ? "order-2 lg:order-2" : "order-2 lg:order-1"
              }`}
            >
              <img
                src={SECTION_IMAGES[section.id] || SECTION_IMAGES.scope}
                alt={section.title}
                className="w-full h-full object-cover min-h-[300px] lg:min-h-0"
              />
            </div>
          </div>
        ))}

      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          {/* GXR-style cards */}
          <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
            {Array.isArray(bottom) &&
              bottom.map((section, index) => {
                const Icon = bottomIcons[section.id] || FileText;
                const num = String(index + 1).padStart(2, "0");
                return (
                  <div
                    key={section.id || index}
                    className={`group relative overflow-hidden p-8 lg:p-10 bg-white border border-gray-200 hover:border-[#E8792B]/50 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-500 ${
                      isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                    }`}
                    style={{ transitionDelay: `${600 + index * 120}ms` }}
                  >
                    <div className="absolute -bottom-8 -right-2 font-black text-[160px] lg:text-[200px] leading-none text-[#E8792B]/[0.07] pointer-events-none select-none">
                      {num}
                    </div>

                    <span className="absolute top-0 left-0 h-px w-12 bg-[#E8792B] transition-all duration-500 group-hover:w-full" />

                    <div className="relative inline-flex items-center justify-center h-12 w-12 border border-[#E8792B]/40 bg-[#E8792B]/5 transition-all duration-300 group-hover:bg-[#E8792B]/10 group-hover:border-[#E8792B] group-hover:shadow-[0_0_24px_rgba(232,121,43,0.25)]">
                      <Icon className="h-5 w-5 text-[#E8792B]" strokeWidth={2} />
                    </div>

                    <div className="relative mt-8 text-[11px] font-bold tracking-[0.3em] text-[#E8792B]/70">
                      · {num} ·
                    </div>

                    <h3 className="relative mt-4 text-[20px] lg:text-[22px] font-bold leading-[1.2] text-[#1a1a2e] tracking-tight">
                      {section.title}
                    </h3>

                    <p className="relative mt-5 text-gray-600 text-[14px] leading-relaxed max-w-[36ch]">
                      {section.text}
                    </p>

                    <div className="relative mt-8 flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-transparent group-hover:text-[#E8792B] transition-all duration-300">
                      <span className="h-px w-6 bg-[#E8792B] transition-all duration-300 group-hover:w-10" />
                      <ArrowRight className="h-3.5 w-3.5" />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdditionalSections;
